// Extracted from src/pages/staff/ZoneForm.js (Link Exhibits to Zone section)
// in PR 12 ST 5. Zone-specific — no analog in features/exhibits/.
// Presentational + controlled: orchestrator (ST 6) owns selectedExhibitIds
// and provides the toggle callback. Exhibit list itself is fetched via
// useExhibitList from @shared/api/hooks/useExhibits and passed in as
// `allExhibits` so this component stays pure render.
//
// #4: with many exhibits the flat grid was hard to work with, so the list
// carries local-only view state — a search box (title TC/EN/SC + code) and an
// All / Linked / Available / Taken filter, plus a prominent "N linked" count.
// These are pure presentation (they never touch the saved exhibit_ids), so
// they live here rather than in the orchestrator.
//
// F-R (2026-09-08): `allExhibits` is now the SERVER search result (newest
// `resultLimit` rows matching the search box, via `onQueryChange`), and the
// zone's own links arrive separately as `linkedExhibits`. The two are merged
// into one pool so Linked always renders, however many exhibits exist. The
// client-side filter still runs on top — harmless on server-narrowed rows, and
// it keeps the Linked tab searchable without a round trip.
//
// Ordering (2026-09-07): the "Linked" view doubles as the reorder surface —
// single column, sorted by `selectedExhibitIds` (= `zone.exhibit_ids` = the
// visitor order), with a 1-based position and up/down arrows that hand the
// orchestrator the full re-sequenced id list via `onReorder` (same contract as
// MediaKindTab). Arrows hide while a search is active — moving past hidden
// neighbours would be confusing. Other views keep the 2-column membership grid.
//
// Available vs Taken (ADR-0025 single-parent): each ExhibitListItem carries the
// authoritative `zone_id` / `zone_title_tc` of its holding zone. An exhibit in
// ANOTHER zone is "Taken" — greyed out and non-selectable here, since linking it
// would 409 at save. An exhibit in no zone (or in THIS zone) is selectable.

import { useMemo, useState, type FC } from "react";
import { ArrowDown, ArrowUp, Ban, Check, Search } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { assetThumbUrl } from "@shared/api/assets";
import type { ExhibitListItem } from "@shared/api/exhibits";

// Linked / Available / Taken partition every exhibit exactly once (2026-09-08:
// the former "All" tab was dropped — its linked-first ordering read as "every
// exhibit is checked", and the three tabs already cover the whole set).
export type FilterMode = "linked" | "available" | "taken";

export interface ExhibitsListProps {
  allExhibits: ExhibitListItem[];
  selectedExhibitIds: string[];
  onToggle: (exhibitId: string) => void;
  /**
   * The id of the zone being edited (undefined when creating). An exhibit whose
   * `zone_id` equals this is held by THIS zone, so it's selectable, not "Taken".
   */
  currentZoneId?: string;
  /**
   * Linked exhibits that are purged (tombstones) — never selectable here, shown
   * read-only as a record of what the zone once contained (#5).
   */
  purgedLinked?: ExhibitListItem[];
  /**
   * Visitor-order editing (Linked view). Receives the FULL re-sequenced
   * `selectedExhibitIds`; omit to render the Linked view read-only.
   */
  onReorder?: (exhibitIds: string[]) => void;
  /** Lock the arrows (archived / purged zone) while keeping the list visible. */
  reorderDisabled?: boolean;
  /**
   * The exhibits query is still in flight. Renders a loading row instead of
   * the empty-state copy — with hundreds of exhibits in production the fetch
   * takes a moment, and "0 linked / No exhibits available" read as data loss.
   */
  loading?: boolean;
  /** The zone's linked exhibits (from the zone endpoint) — merged into the
   *  pool so Linked works even when they're not in the search results. */
  linkedExhibits?: ExhibitListItem[];
  /** Search-box changes (raw, undebounced). Presence switches the picker to
   *  server-search mode: controls stay visible on an empty result set. */
  onQueryChange?: (query: string) => void;
  /** A server search is in flight. */
  searching?: boolean;
  /** Server cap on `allExhibits`; hitting it shows a refine-your-search hint. */
  resultLimit?: number;
  /** Tab changes — the orchestrator turns Available / Taken into a
   *  server-side `membership` filter so each tab gets its own window. */
  onFilterChange?: (mode: FilterMode) => void;
  /** Initial tab: Linked when editing a zone, Available when creating one. */
  defaultFilter?: FilterMode;
  /** Server totals for the Available / Taken chips (independent of the window). */
  counts?: { unassigned: number; taken: number };
}

function buildImageSrc(coverImageId: string | null | undefined): string | null {
  return coverImageId ? assetThumbUrl(coverImageId) : null;
}

// Same state glyphs as the dashboards — the picker doubles as a publish
// triage surface (batch imports land as draft and are invisible to
// visitors until published).
function stateLabel(state: ExhibitListItem["state"]): string {
  if (state === "published") return "▣ published";
  if (state === "archived") return "▼ archived";
  if (state === "purged") return "✕ purged";
  return "◌ draft";
}

const ExhibitsList: FC<ExhibitsListProps> = ({
  allExhibits,
  selectedExhibitIds,
  onToggle,
  currentZoneId,
  purgedLinked = [],
  onReorder,
  reorderDisabled = false,
  loading = false,
  linkedExhibits = [],
  onQueryChange,
  searching = false,
  resultLimit,
  onFilterChange,
  defaultFilter = "available",
  counts,
}) => {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterMode>(defaultFilter);
  const serverSearch = Boolean(onQueryChange);

  // One pool: the zone's links first (so Linked never depends on the search
  // window), then the search results, de-duplicated by id.
  const pool = useMemo(() => {
    const seen = new Set<string>();
    const out: ExhibitListItem[] = [];
    for (const e of [...linkedExhibits, ...allExhibits]) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push(e);
    }
    return out;
  }, [linkedExhibits, allExhibits]);

  // Held by a *different* zone than the one being edited → not linkable here.
  const isTakenElsewhere = (e: ExhibitListItem): boolean =>
    Boolean(e.zoneId) && e.zoneId !== currentZoneId;

  // Count only selections still present in the selectable grid; purged links
  // (shown separately, read-only) shouldn't inflate the active count.
  const linkedCount = pool.filter((e) =>
    selectedExhibitIds.includes(e.id),
  ).length;

  // The grid shows exhibits matching the active filter AND the search query.
  // The taken check is inlined (not the render helper) so exhaustive-deps is
  // satisfied by the listed primitives alone.
  const visibleExhibits = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = pool.filter((e) => {
      const isLinked = selectedExhibitIds.includes(e.id);
      const taken =
        !isLinked && Boolean(e.zoneId) && e.zoneId !== currentZoneId;
      if (filter === "linked" && !isLinked) return false;
      if (filter === "available" && (isLinked || taken)) return false;
      if (filter === "taken" && !taken) return false;
      if (q) {
        const haystack =
          `${e.code} ${e.titleTc} ${e.titleEn} ${e.titleSc}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    if (filter !== "linked") return matching;
    // Linked view = visitor order.
    const position = new Map(selectedExhibitIds.map((id, i) => [id, i]));
    return [...matching].sort(
      (a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0),
    );
  }, [pool, selectedExhibitIds, filter, query, currentZoneId]);

  const isLinkedView = filter === "linked";
  const hasQuery = query.trim().length > 0;
  const showArrows = isLinkedView && Boolean(onReorder) && !hasQuery;

  // Swap with the neighbour in the VISIBLE linked order, applied to the full
  // selection (which may also hold purged ids that never render here).
  const moveExhibit = (exhibitId: string, direction: -1 | 1) => {
    if (!onReorder) return;
    const visibleIds = visibleExhibits.map((e) => e.id);
    const pos = visibleIds.indexOf(exhibitId);
    const neighbor = visibleIds[pos + direction];
    if (neighbor === undefined) return;
    const full = [...selectedExhibitIds];
    const a = full.indexOf(exhibitId);
    const b = full.indexOf(neighbor);
    if (a < 0 || b < 0) return;
    [full[a], full[b]] = [full[b], full[a]];
    onReorder(full);
  };

  const withCount = (label: string, n: number | undefined) =>
    n === undefined ? label : `${label} · ${n}`;
  const filterChips: { mode: FilterMode; label: string }[] = [
    { mode: "linked", label: `Linked · ${linkedCount}` },
    { mode: "available", label: withCount("Available", counts?.unassigned) },
    { mode: "taken", label: withCount("Taken", counts?.taken) },
  ];

  return (
    <div
      className="space-y-4 p-6 bg-white border border-border rounded-lg"
      data-testid="exhibits-list-section"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading font-semibold">Link Exhibits to Zone</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Select exhibits to include in this zone.
          </p>
        </div>
        <span
          className="shrink-0 inline-flex items-center rounded-full bg-primary/10 text-primary px-3 py-1 text-sm font-medium"
          data-testid="linked-count"
        >
          {loading ? "Loading…" : `${linkedCount} linked`}
        </span>
      </div>

      {loading ? (
        <div
          className="text-center py-8 text-muted-foreground"
          data-testid="exhibits-loading"
        >
          Loading exhibits…
        </div>
      ) : pool.length === 0 && !serverSearch ? (
        <div className="text-center py-8 text-muted-foreground">
          No exhibits available. Create some exhibits first.
        </div>
      ) : (
        <>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  onQueryChange?.(e.target.value);
                }}
                placeholder="Search by title or code…"
                aria-label="Search exhibits"
                className="pl-9"
                data-testid="exhibit-search"
              />
            </div>
            <div
              className="flex flex-wrap gap-2"
              role="group"
              aria-label="Filter exhibits"
            >
              {filterChips.map((chip) => {
                const active = filter === chip.mode;
                return (
                  <button
                    key={chip.mode}
                    type="button"
                    onClick={() => {
                      setFilter(chip.mode);
                      onFilterChange?.(chip.mode);
                    }}
                    aria-pressed={active}
                    className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                      active
                        ? "border-primary bg-primary text-white"
                        : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                    data-testid={`exhibit-filter-${chip.mode}`}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>

          {searching && (
            <p
              className="text-xs text-muted-foreground"
              data-testid="exhibits-searching"
            >
              Searching…
            </p>
          )}
          {visibleExhibits.length === 0 ? (
            <div
              className="text-center py-8 text-muted-foreground"
              data-testid="exhibits-empty-filtered"
            >
              {serverSearch && !query.trim() && filter !== "linked"
                ? "Nothing here yet — type a code or title to search."
                : "No exhibits match your search or filter."}
            </div>
          ) : (
            <div
              className={`grid grid-cols-1 gap-3 max-h-96 overflow-y-auto ${
                isLinkedView ? "" : "sm:grid-cols-2"
              }`}
              data-testid={isLinkedView ? "exhibits-linked-order" : undefined}
            >
              {visibleExhibits.map((exhibit, pos) => {
                const isSelected = selectedExhibitIds.includes(exhibit.id);
                const taken = !isSelected && isTakenElsewhere(exhibit);
                const imgSrc = buildImageSrc(exhibit.coverImageId);
                return (
                  // Split interactions: the NAME opens the exhibit form in a
                  // new tab (so unsaved zone edits survive the round-trip);
                  // only the CIRCLE toggles the link. The card body itself is
                  // inert — no more accidental link-toggles.
                  <div
                    key={exhibit.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                      taken
                        ? "border-border bg-muted/40 opacity-60"
                        : isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border"
                    }`}
                    data-testid={`exhibit-row-${exhibit.id}`}
                  >
                    {isLinkedView && (
                      <span
                        className="w-6 shrink-0 text-right text-sm tabular-nums text-muted-foreground"
                        data-testid={`exhibit-position-${exhibit.id}`}
                      >
                        {pos + 1}
                      </span>
                    )}
                    {showArrows && (
                      <div className="flex flex-col -my-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1"
                          disabled={reorderDisabled || pos === 0}
                          onClick={() => moveExhibit(exhibit.id, -1)}
                          aria-label={`Move ${exhibit.titleTc} up`}
                          data-testid={`exhibit-move-up-${exhibit.id}`}
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1"
                          disabled={
                            reorderDisabled ||
                            pos === visibleExhibits.length - 1
                          }
                          onClick={() => moveExhibit(exhibit.id, 1)}
                          aria-label={`Move ${exhibit.titleTc} down`}
                          data-testid={`exhibit-move-down-${exhibit.id}`}
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={exhibit.titleTc}
                        loading="lazy"
                        className="w-12 h-12 object-cover rounded"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-muted rounded flex items-center justify-center text-xs text-muted-foreground">
                        No img
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <a
                        href={`/staff/exhibit/${exhibit.id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-sm truncate block hover:underline hover:text-primary"
                        title="Open the exhibit form in a new tab"
                        data-testid={`exhibit-open-${exhibit.id}`}
                      >
                        {exhibit.titleTc}
                      </a>
                      <div className="text-xs text-muted-foreground">
                        Code: {exhibit.code}
                        <span
                          className={`ml-2 ${
                            exhibit.state === "published"
                              ? "text-green-700"
                              : "text-amber-700"
                          }`}
                          data-testid={`exhibit-state-${exhibit.id}`}
                        >
                          {stateLabel(exhibit.state)}
                        </span>
                      </div>
                      {taken && (
                        <div
                          className="text-xs text-amber-700 mt-0.5 truncate"
                          data-testid={`exhibit-taken-tag-${exhibit.id}`}
                        >
                          In {exhibit.zoneTitleTc ?? "another zone"}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={taken}
                      aria-disabled={taken}
                      aria-pressed={isSelected}
                      aria-label={
                        isSelected
                          ? `Unlink ${exhibit.titleTc}`
                          : `Link ${exhibit.titleTc}`
                      }
                      onClick={taken ? undefined : () => onToggle(exhibit.id)}
                      className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
                        taken
                          ? "border-muted-foreground/20 cursor-not-allowed"
                          : isSelected
                            ? "border-primary bg-primary text-white cursor-pointer"
                            : "border-muted-foreground/30 cursor-pointer hover:border-primary"
                      }`}
                      data-testid={`exhibit-select-${exhibit.id}`}
                    >
                      {taken ? (
                        <Ban className="w-3.5 h-3.5 text-muted-foreground/60" />
                      ) : (
                        isSelected && <Check className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {serverSearch &&
            resultLimit !== undefined &&
            allExhibits.length >= resultLimit &&
            !isLinkedView && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="exhibits-result-limit-hint"
              >
                Showing the newest {resultLimit} matches — refine your search to
                find older exhibits.
              </p>
            )}
          {isLinkedView && onReorder && (
            <p className="text-xs text-muted-foreground">
              {hasQuery ? (
                <span data-testid="exhibits-reorder-search-hint">
                  Clear the search to reorder.
                </span>
              ) : (
                <>
                  Visitor order — exhibits appear on the zone page in this
                  sequence, filling left to right. Reorder with the arrows, then
                  Update the zone.
                </>
              )}
            </p>
          )}
        </>
      )}

      {purgedLinked.length > 0 && (
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm"
          data-testid="purged-linked-exhibits"
        >
          <p className="font-medium text-amber-900">
            {purgedLinked.length} linked{" "}
            {purgedLinked.length === 1 ? "exhibit is" : "exhibits are"} purged
          </p>
          <ul className="mt-1 space-y-0.5 text-amber-800">
            {purgedLinked.map((e) => (
              <li key={e.id} data-testid={`purged-linked-${e.id}`}>
                {e.code} — {e.titleTc}{" "}
                <span className="text-amber-600">(assets removed)</span>
              </li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-amber-700">
            Kept as a record of what this zone once contained; they don't count
            toward the zone's live exhibits and can't be re-selected.
          </p>
        </div>
      )}
    </div>
  );
};

export default ExhibitsList;
